import React, { useState, useEffect } from 'react';
import { reduxForm, InjectedFormProps, SubmitHandler } from 'redux-form';
import { compose } from 'ramda';

import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';

import ReviewAnswers from './ReviewAnswers';
import QuestionaireQuestions from './QuestionaireQuestions';
import Indicator from './Indicator';
import { QuestionType } from 'store/modules/Quesionnare/types';
import { Selections } from './models';
import { Button } from 'components/Base';
import { isArray } from 'lodash';

const useStyles = makeStyles({
  root: {
    minWidth: 275,
    minHeight: '546px',
    height: 'max-content',
    position: 'relative',
    maxWidth: '1000px',
    boxShadow: 'none',
    left: '50%',
    transform: 'translateX(-50%)',
    '@media (max-width: 671px)': {
      height: '600px'
    },
    '@media (max-width: 461px)': {
      height: '600px'
    },
    '@media (max-width: 439px)': {
      height: '680px'
    },
    '@media (max-width: 386px)': {
      height: '700px'
    },
    overflowY: 'auto',
    borderRadius: '25px'
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%'
  },
  pos: {
    marginBottom: 12
  },
  answer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'center',
    alignItems: 'center',
    width: 'calc(100% - 64px)',
    maxWidth: '1000px',
    height: '50px',
    transition: 'all 0.3s ease-in-out',
    position: 'relative',
    marginTop: '12px',
    marginBottom: '12px',
    borderRadius: '8px',
    backgroundColor: 'lightblue',
    '@media (max-width: 1000px)': {
      marginTop: '10px',
      marginBottom: '10px',
      width: 'calc(100% - 80px)',
      height: '40px'
    }
  },
  question: {
    position: 'absolute',
    width: 'calc(100% - 50px)',
    transition: 'all 0.3s ease-in-out'
  },
  indicator: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
    justifyContent: 'center',
    marginTop: '12px'
  },
  buttonsContainer: {
    width: 'calc(100% - 124px)',
    marginRight: '64px',
    marginLeft: '60px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'end',
    paddingBottom: '16px'
  },
  review: {
    height: '100vh',
    width: '100vw',
    backgroundColor: 'yellow',
    zIndex: 200
  },
  reviewContainer: {
    overflowY: 'auto',
    height: '100%'
  },
  questionCard: {
    padding: '4px',
    border: '1px solid #F0F0F0',
    borderRadius: '25px',
    marginBottom: '4px'
  },
  answerCard: {
    padding: '4px',
    border: '1px solid #F0F0F0',
    backgroundColor: '#F0F0F0',
    borderRadius: '8px',
    marginBottom: '4px'
  },
  summary: {
    marginLeft: '48px',
    marginRight: '48px',
    marginTop: '100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    fontSize: '20px',
    '@media (max-width: 1000px)': {
      fontSize: '18px',
      marginLeft: '10px',
      marginRight: '10px',
      marginTop: '10px'
    }
  },
  startBtnContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '@media (max-width: 1000px)': {
      justifyContent: 'center'
    }
  }
});

export class Responses {
  ['question_id']!: number;
  ['response_id']!: number | number[];
}

interface QuestionaireFormProps extends Partial<InjectedFormProps> {
  handleSubmit: SubmitHandler;
  createResponses: Function;
  surveyData: QuestionType[];
  customerName?: string;
  userEmail?: string;
  submitted: boolean;
  isSubmitting: boolean;
  successful: boolean;
  organization?: string;
  title: string;
  hasSummary: boolean;
  displayTextarea?: Function;
  userResponses?: any[];
  complete?: Function;
  showReviewButtons: boolean;
  children: any;
  updateSelections?: Function;
  Result?: any;
}

const QuestionaireForm = ({
  handleSubmit,
  surveyData,
  customerName = '',
  userEmail = '',
  submitted,
  isSubmitting,
  successful,
  organization = '',
  title,
  hasSummary = true,
  displayTextarea = () => false,
  userResponses = [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  complete = () => {},
  showReviewButtons = true,
  children,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateSelections = () => {},
  Result = null
}: QuestionaireFormProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selections, setSelections] = useState({} as Selections);
  const [editing, setEditing] = useState(false);
  const [responses, setResponses] = useState([] as Responses[]);
  const [started, setStarted] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (!updateSelections) return;
    updateSelections(responses);
  }, [responses]);

  useEffect(() => {
    if (userResponses.length && !editing) {
      setSelectedIndex(surveyData.length);
      setStarted(true);
      const surveyDataSelections: any = {};
      userResponses
        .sort((a, b) => a.question_id - b.question_id)
        .forEach((response: any, key: number) => {
          const question = surveyData.find(
            question => question.id == response.question_id
          );

          if (!question) return;
          if (isArray(response.response_id)) {
            surveyDataSelections[`Q${key + 1}`] = {
              text: response.response_id
                .map((id: any) => {
                  const answer = question.responses.find(
                    (possibleResponse: any) => possibleResponse.id == id
                  );
                  return answer ? answer.text : '';
                })
                .join(', '),
              responses: question.responses.map(
                (response: any) => response.text
              ),
              response_ids: response.response_id,
              details: response.details || ''
            };
          } else {
            const answer = question.responses.find(
              (possibleResponse: any) =>
                possibleResponse.id == response.response_id
            );

            surveyDataSelections[`Q${key + 1}`] = {
              text: answer ? answer.text : '',
              details: answer ? response.details || '' : ''
            };
          }
        });
      setSelections(surveyDataSelections);
    }

    if (selectedIndex > surveyData.length - 1 && !editing && started) {
      complete();
    }

    if (selectedIndex === surveyData.length) setEditing(false);
    if (!hasSummary) setStarted(true);
    // eslint-disable-next-line
  }, [selectedIndex, surveyData.length]);

  const createResponses = (i: number, response: number | number[]) => {
    const r = responses as Responses[];
    r[i] = { question_id: i + 1, response_id: response };
    setResponses(r);
  };

  const select = (
    question: string,
    answer: string,
    index: number,
    response: number,
    multiselect = false
  ) => {
    if (!multiselect) {
      setSelections({
        ...selections,
        [question]: {
          text: answer
        }
      });
      createResponses(index, response);
    } else {
      const currentResponses = selections[question]?.responses || [];
      const updatedResponses = currentResponses.includes(answer)
        ? currentResponses.filter((response: string) => response !== answer)
        : [...currentResponses, answer];

      const responseIds = selections[question]?.response_ids || [];
      const updatedResponseIds = responseIds.includes(response)
        ? responseIds.filter((id: number) => id !== response)
        : [...responseIds, response];

      setSelections({
        ...selections,
        [question]: {
          text: updatedResponses.join(', '),
          responses: updatedResponses,
          response_ids: updatedResponseIds
        }
      });
      createResponses(index, updatedResponseIds);
    }
  };

  const setSelectionDetails = (question: string, details: string) => {
    setSelections({
      ...selections,
      [question]: {
        ...selections[question],
        details
      }
    });
  };

  const edit = () => {
    setSelections({});
    setSelectedIndex(0);
    setEditing(true);
  };

  const indicatorNavigation = (index: number) => {
    setSelectedIndex(index);
  };

  const isNextButtonDisabled =
    Object.keys(selections).length - 1 < selectedIndex;

  return (
    <div style={{ overflow: 'hidden' }}>
      {surveyData.length ? (
        <Card className={classes.root} style={{ position: 'relative' }}>
          <CardContent>
            <div className={classes.title}>
              <Typography
                component="h2"
                style={{ fontWeight: 600, marginRight: 10 }}
              >
                {`${title} `}
              </Typography>
              <Typography component="h2">{customerName}</Typography>
            </div>

            {started && (
              <Indicator
                responses={responses}
                indicatorNavigation={indicatorNavigation}
                selectedIndex={selectedIndex}
                surveyData={surveyData}
              />
            )}

            {started ? (
              <QuestionaireQuestions
                surveyData={surveyData}
                selectedIndex={selectedIndex}
                selections={selections}
                handleSubmit={handleSubmit}
                select={select}
                setSelectionDetails={setSelectionDetails}
                createResponses={createResponses}
                displayTextarea={displayTextarea}
              />
            ) : (
              <div className={classes.summary}>
                {children || (
                  <>
                    <h4>
                      Please answer the following questions which ask about your
                      thoughts, attitudes and experiences when making financial
                      investments and decisions. There are no right or wrong
                      answers. They are to help us assess your attitude to risk
                      in making investments.
                    </h4>
                    <ul>
                      <li>
                        Don’t spend too much time considering – your first
                        response is probably best.
                      </li>
                      <li>
                        Please answer every question even if you feel it doesn’t
                        really apply to you.
                      </li>
                    </ul>
                  </>
                )}
                <div className={classes.startBtnContainer}>
                  <Button
                    type="submit"
                    onClick={() => setStarted(true)}
                    style={{ marginTop: '64px' }}
                  >
                    Start
                  </Button>
                </div>
              </div>
            )}

            {selectedIndex > surveyData.length - 1 && !editing && started ? (
              <ReviewAnswers
                userEmail={userEmail}
                surveyData={surveyData}
                selections={selections}
                handleSubmit={handleSubmit}
                edit={edit}
                submitted={submitted}
                isSubmitting={isSubmitting}
                successful={successful}
                organization={organization}
                showButtons={showReviewButtons}
                Result={Result}
              />
            ) : null}
          </CardContent>
          {selectedIndex <= surveyData.length - 1 && started ? (
            <div className={classes.buttonsContainer}>
              <Button
                color="primary"
                type="submit"
                onClick={() => setSelectedIndex(selectedIndex - 1)}
                disabled={selectedIndex === 0}
              >
                Previous
              </Button>
              <Button
                type="submit"
                onClick={() => setSelectedIndex(selectedIndex + 1)}
                disabled={isNextButtonDisabled}
              >
                Next
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
};

export default compose(
  reduxForm({
    form: 'questionaire'
  })
)(QuestionaireForm as any);
